import { useState, useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Navigation } from "swiper/modules";
import FarmCard from "./FarmCard";
import SkeletonCard from "../common/SkeletonCard";
import FarmCardStyle from "../../assets/stylesheets/FarmCard.module.css";
import logger from "../../utils/logger";
import { useFarmImagePreloader } from "../../hooks/useImagePreloader";

const log = logger.create("SwiperFarmCardList");

/**
 * SwiperFarmCardList component renders farm cards in Swiper slides with staggered loading.
 * This component returns the complete Swiper with wrapper div.
 * @param {Object} props
 * @param {Array} props.farms - Array of farm objects
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onFarmClick - Click handler for farm cards
 * @param {number} props.skeletonCount - Number of skeleton cards to show while loading
 * @param {Object} props.prevRef - Ref for previous navigation button
 * @param {Object} props.nextRef - Ref for next navigation button
 * @param {Function} props.updateNavState - Function to update navigation state
 * @returns {JSX.Element} Complete Swiper carousel with wrapper
 */
function SwiperFarmCardList({ farms, loading, onFarmClick, skeletonCount = 6, prevRef, nextRef, updateNavState }) {
  const [visibleFarms, setVisibleFarms] = useState(new Set());
  const swiperRef = useRef(null);
  
  const FARM_REVEAL_DELAY_MS = 25; // Time between each farm card reveal

  // Use custom hook for image preloading
  const preloadComplete = useFarmImagePreloader(farms, loading, 3, 2000);

  // When preloading is complete, start showing farms sequentially
  useEffect(() => {
    if (!loading && farms.length > 0 && preloadComplete) {
      // Reset visible farms
      setVisibleFarms(new Set());
      
      // Show farms one by one with delays
      farms.forEach((farm, index) => {
        setTimeout(() => {
          setVisibleFarms(prev => new Set([...prev, farm.id]));
        }, index * FARM_REVEAL_DELAY_MS);
      });
    } else if (loading || farms.length === 0) {
      // Reset visible farms when loading or no farms
      setVisibleFarms(new Set());
    }
  }, [loading, farms, preloadComplete]);

  return (
    <div className={FarmCardStyle["events-carousel-wrapper"]}>
      <Swiper
        ref={swiperRef}
        modules={[FreeMode, Navigation]}
        freeMode
        spaceBetween={10}
        slidesPerView={3.4}
        breakpoints={{
          0: { slidesPerView: 2 },
          480: { slidesPerView: 2 },
          768: { slidesPerView: 3 },
          1024: { slidesPerView: 4 }
        }}
        onBeforeInit={swiper => {
          if (prevRef?.current && nextRef?.current) {
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;
          }
          if (updateNavState) {
            updateNavState(swiper);
          }
        }}
        onSlideChange={swiper => {
          if (updateNavState) {
            updateNavState(swiper);
          }
        }}
        className={FarmCardStyle["swiper-carousel"]}
      >
        {loading
          ? Array.from({ length: skeletonCount }).map((_, index) => (
              <SwiperSlide key={`skeleton-${index}`}>
                <SkeletonCard />
              </SwiperSlide>
            ))
          : farms.map(farm => (
              <SwiperSlide key={farm.id}>
                {visibleFarms.has(farm.id) ? (
                  <div
                    className={FarmCardStyle["cursor-pointer"]}
                    onClick={() => onFarmClick(farm)}
                  >
                    <FarmCard farm={farm} />
                  </div>
                ) : (
                  <SkeletonCard />
                )}
              </SwiperSlide>
            ))
        }
      </Swiper>
    </div>
  );
}

export default SwiperFarmCardList;
